import React, { useEffect } from 'react';
import NavBar from './NavBar';
import './About.css';

function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about">
      <NavBar />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1>About Orchestral AI</h1>
          <p className="about-subtitle">Built by researchers, for researchers</p>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <div className="about-container">
          <h2 className="about-section-title">Our Story</h2>
          <p className="about-text">
            We first started developing Orchestral in 2023 as a private tool to build agents and assist with our own work and research.
            What began as a personal project quickly evolved into a sophisticated framework as we discovered what features we truly needed
            for real-world agent orchestration.
          </p>
          <p className="about-text">
            Over the years, we've refined the platform through countless experiments, research projects, and production deployments.
            Every feature in Orchestral exists because we needed it ourselves—from automatic cost tracking to hierarchical agent systems
            to seamless provider switching. This is a tool built by practitioners who live and breathe agentic AI.
          </p>
        </div>
      </section>

      {/* Team Section */}
      <section className="about-team">
        <div className="about-container">
          <h2 className="about-section-title">Two Brothers</h2>

          <div className="team-photo-container">
            <img
              src="/alex_and_jake.jpeg"
              alt="Alex Roman and Jake Roman"
              className="team-photo"
            />
          </div>

          <div className="team-members">
            <div className="team-member">
              <h3>Alex Roman</h3>
              <p className="member-bio">
                <p>
                Alex holds a PhD in theoretical physics and is formerly a professor of Machine Learning. He is now working on agent safety, shutdownability, 
                and advanced reasoning systems.
                </p>

                <p>
                His work focuses on building agents with explicit probabilistic belief representations and
                understanding how to safely interrupt and correct agent behavior. He uses Orchestral daily
                to automate experiments, reproduce papers, and accelerate his research workflow.
                </p>
              </p>
              <div className="member-links">
                <a href="https://www.linkedin.com/in/romanphd/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <a href="https://github.com/alexr314" target="_blank" rel="noopener noreferrer">GitHub</a>
                <a href="https://alexroman.me" target="_blank" rel="noopener noreferrer">Website</a>
              </div>
            </div>

            <div className="team-member">
              <h3>Jake Roman</h3>
              <p className="member-bio">
                <p>
                Jake is a computer scientist, software engineer, and researcher specializing in building robust, production-ready
                AI systems. He brings his expertise in system architecture, performance optimization, cybersecurity, and elegant
                API design to Orchestral. 
                </p>
                
                <p>
                His focus is on making advanced agent capabilities accessible through
                clean, intuitive interfaces, and beautiful object-oriented code. He uses Orchestral to build and deploy cool AI applications.
                </p>
              </p>
              <div className="member-links">
                <a href="https://www.linkedin.com/in/jakemroman/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <a href="https://github.com/jakeroman" target="_blank" rel="noopener noreferrer">GitHub</a>
                <a href="https://jakeroman.com" target="_blank" rel="noopener noreferrer">Website</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="about-philosophy">
        <div className="about-container">
          <h2 className="about-section-title">Our Philosophy</h2>
          <blockquote className="about-quote">
            "Civilization advances by extending the number of important operations which we can perform without thinking of them."
            <cite>— Alfred North Whitehead</cite>
          </blockquote>
          <p className="about-text">
            We believe that both human and AI researchers benefit from building tools that abstract previously difficult operations.
            Agents can be created to handle complex operations and called by higher-level agents that orchestrate sophisticated
            research workflows. Such hierarchical systems show great promise in extending the capabilities and coherence horizon
            of the base LLMs composing them.
          </p>
          <p className="about-text">
            Orchestral embodies this philosophy by making agent creation effortless. What once required hundreds of lines of
            boilerplate and deep knowledge of provider APIs now takes just a few lines of code. We've abstracted away the complexity
            so you can focus on what matters: building intelligent agents that solve real problems.
          </p>
        </div>
      </section>

      {/* Research Section */}
      <section className="about-research">
        <div className="about-container">
          <h2 className="about-section-title">Research & Safety</h2>
          <p className="about-text">
            Orchestral was born from active research in agent safety and reliability. Our work includes:
          </p>
          <ul className="about-list">
            <li>Shutdownability research examining how agents can be safely interrupted or corrected</li>
            <li>Adversarial guardrails using agents to red-team security systems in controlled environments</li>
            <li>Information degradation analysis in iterative summarization with implications for agent memory</li>
            <li>Advanced agents with explicit Bayesian belief representations and dynamic belief updating</li>
          </ul>
          <p className="about-text">
            These research directions inform Orchestral's design, ensuring the platform supports not just powerful agents,
            but safe and reliable ones.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-cta-content">
          <h2>Ready to Build?</h2>
          <p>Join us in advancing the future of AI agent orchestration</p>
          <a href="/" className="about-cta-button">Get Started</a>
        </div>
      </section>
    </div>
  );
}

export default About;
